
import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type Event = Tables<"events">;
export type EventAttendee = Tables<"event_attendees">;
export type EventInsert = TablesInsert<"events">;
export type EventUpdate = TablesUpdate<"events">;
export type AttendeeInsert = TablesInsert<"event_attendees">;
export type AttendeeUpdate = TablesUpdate<"event_attendees">;

export interface EventWithAttendees extends Event {
  attendees: (EventAttendee & {
    user: {
      username: string;
      email: string;
    };
  })[];
}

export const eventService = {
  async getEvents(): Promise<EventWithAttendees[]> {
    console.log("Fetching events...");
    
    const { data, error } = await supabase
      .from("events")
      .select(`
        *,
        attendees:event_attendees(
          *,
          user_profiles(
            display_name
          )
        )
      `)
      .order("event_date", { ascending: true });

    if (error) {
      console.error("Error fetching events:", error);
      throw error;
    }
    
    console.log("Raw events data:", data);
    
    // Transform the data to match the expected interface
    const transformedData = data?.map(event => ({
      ...event,
      attendees: event.attendees?.map(attendee => ({
        ...attendee,
        user: {
          username: attendee.user_profiles?.display_name || 'Unknown User',
          email: '' // We don't have email in user_profiles, so we'll use empty string
        }
      })) || []
    })) || [];

    console.log("Transformed events data:", transformedData);
    return transformedData;
  },

  async getEvent(id: string): Promise<EventWithAttendees | null> {
    const { data, error } = await supabase
      .from("events")
      .select(`
        *,
        attendees:event_attendees(
          *,
          user_profiles(
            display_name
          )
        )
      `)
      .eq("id", id)
      .single();

    if (error) {
      console.error("Error fetching event:", error);
      throw error;
    }
    
    // Transform the data to match the expected interface
    if (!data) return null;
    
    const transformedData = {
      ...data,
      attendees: data.attendees?.map(attendee => ({
        ...attendee,
        user: {
          username: attendee.user_profiles?.display_name || 'Unknown User',
          email: '' // We don't have email in user_profiles, so we'll use empty string
        }
      })) || []
    };

    return transformedData;
  },

  async createEvent(event: EventInsert): Promise<Event> {
    console.log("Creating event with data:", event);
    
    const { data, error } = await supabase
      .from("events")
      .insert(event)
      .select()
      .single();

    if (error) {
      console.error("Error creating event:", error);
      throw error;
    }
    
    console.log("Created event:", data);
    return data;
  },

  async updateEvent(id: string, updates: EventUpdate): Promise<Event> {
    const { data, error } = await supabase
      .from("events")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase
      .from("events")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  async requestAttendance(eventId: string): Promise<EventAttendee> {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError || !userData.user) {
      throw new Error("User not authenticated");
    }

    const { data, error } = await supabase
      .from("event_attendees")
      .insert({
        event_id: eventId,
        user_id: userData.user.id,
        status: "pending"
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateAttendeeStatus(attendeeId: string, status: "approved" | "declined" | "pending"): Promise<EventAttendee> {
    const { data, error } = await supabase
      .from("event_attendees")
      .update({ status })
      .eq("id", attendeeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async promoteToAdmin(attendeeId: string): Promise<EventAttendee> {
    const { data, error } = await supabase
      .from("event_attendees")
      .update({ is_admin: true })
      .eq("id", attendeeId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async removeAttendee(attendeeId: string): Promise<void> {
    const { error } = await supabase
      .from("event_attendees")
      .delete()
      .eq("id", attendeeId);

    if (error) throw error;
  }
};
